import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, IconButton, Typography, Tooltip, Modal, useTheme } from '@mui/material';
import { CloseFullscreen } from '@mui/icons-material';
import type { FundingActivityChartProps, ZoomLevel, MetricType, ChartDataPoint } from './types';
import { CONFIG } from './constants';
import { calculateTickValues, currencyFormatter } from './utils';
import { useProcessedData, usePanningState, useZoomControls, useEventHandlers } from './hooks';
import { ChartRenderer, ChartControls, ChartHeader } from './components';

export const FundingActivityChart: React.FC<FundingActivityChartProps> = ({
    data,
    height = CONFIG.DEFAULT_HEIGHT,
    defaultMetric = 'amount'
}) => {
    const theme = useTheme();
    const [metric, setMetric] = useState<MetricType>(defaultMetric);
    const [isExpanded, setIsExpanded] = useState(false);
    const [tempZoomLevel, setTempZoomLevel] = useState<ZoomLevel>('month');

    // Zoom positioning state
    const [targetDateForZoom, setTargetDateForZoom] = useState<Date | null>(null);
    const [cursorXForZoom, setCursorXForZoom] = useState<number | null>(null);
    const [isButtonZoom, setIsButtonZoom] = useState(false);

    // Data processing
    const aggregatedData = useProcessedData(data, tempZoomLevel);
    const panningState = usePanningState(tempZoomLevel, aggregatedData, isExpanded);

    // Calculate visible data
    const visibleData = useMemo(() => {
        const windowSizes = isExpanded ? CONFIG.EXPANDED_WINDOW_SIZES : CONFIG.WINDOW_SIZES;
        const windowSize = windowSizes[tempZoomLevel];

        if (aggregatedData.length <= windowSize) {
            return aggregatedData;
        }

        const currentOffset = panningState.tempOffset ?? panningState.panOffset;
        const startIndex = Math.max(0, Math.floor(currentOffset));
        const endIndex = Math.min(aggregatedData.length, startIndex + windowSize);
        return aggregatedData.slice(startIndex, endIndex);
    }, [aggregatedData, panningState.panOffset, panningState.tempOffset, tempZoomLevel, isExpanded]);

    // Zoom change handler
    const handleZoomChange = useCallback((newLevel: ZoomLevel, targetDate?: Date, isButtonClick = false, cursorX?: number) => {
        setTempZoomLevel(newLevel);
        if (targetDate) {
            setTargetDateForZoom(targetDate);
            setIsButtonZoom(isButtonClick);
            if (cursorX !== undefined) {
                setCursorXForZoom(cursorX);
            }
        }
    }, []);

    // Initialize hooks
    const zoomControls = useZoomControls(aggregatedData, panningState, visibleData, isExpanded, handleZoomChange);
    const eventHandlers = useEventHandlers(panningState, aggregatedData, tempZoomLevel, isExpanded);

    // Sync zoom level
    useEffect(() => {
        setTempZoomLevel(zoomControls.zoomLevel);
    }, [zoomControls.zoomLevel]);

    // Chart data preparation
    const chartData: ChartDataPoint[] = useMemo(() => {
        if (data.length === 0) return [];

        if (visibleData.length === 0) {
            return [{
                id: 'No Data',
                value: 0,
                amount: 0,
                count: 0,
                tooltipLabel: 'No data available',
            }];
        }

        return visibleData.map(item => ({
            id: item.label,
            value: metric === 'amount' ? item.amount : item.count,
            amount: item.amount,
            count: item.count,
            tooltipLabel: `${currencyFormatter(item.amount, true)} • ${item.count} deal${item.count !== 1 ? 's' : ''}`,
        }));
    }, [visibleData, metric, data.length]);

    const tickValues = useMemo(() => calculateTickValues(chartData, metric), [chartData, metric]);

    // Calculate capabilities
    const windowSizes = isExpanded ? CONFIG.EXPANDED_WINDOW_SIZES : CONFIG.WINDOW_SIZES;
    const windowSize = windowSizes[zoomControls.zoomLevel];
    const canPan = aggregatedData.length > windowSize;

    // Event handlers
    const handleMetricChange = useCallback((_: React.MouseEvent<HTMLElement>, newMetric: MetricType | null) => {
        if (newMetric !== null) {
            setMetric(newMetric);
        }
    }, []);

    const handleExpandToggle = useCallback(() => {
        setIsExpanded(prev => !prev);
    }, []);

    // Positioning effect after zoom
    useEffect(() => {
        if (targetDateForZoom && aggregatedData.length > 0) {
            let targetIndex = -1;
            let bestMatch = -1;
            let smallestDiff = Infinity;

            aggregatedData.forEach((item, index) => {
                let matches = false;
                const timeDiff = Math.abs(item.period.getTime() - targetDateForZoom.getTime());

                switch (tempZoomLevel) {
                    case 'year':
                        matches = item.period.getFullYear() === targetDateForZoom.getFullYear();
                        break;
                    case 'month':
                        matches = item.period.getFullYear() === targetDateForZoom.getFullYear() &&
                            item.period.getMonth() === targetDateForZoom.getMonth();
                        break;
                    case 'day':
                        matches = item.period.getFullYear() === targetDateForZoom.getFullYear() &&
                            item.period.getMonth() === targetDateForZoom.getMonth() &&
                            item.period.getDate() === targetDateForZoom.getDate();
                        break;
                }

                if (matches) {
                    targetIndex = index;
                    return;
                }

                if (timeDiff < smallestDiff) {
                    smallestDiff = timeDiff;
                    bestMatch = index;
                }
            });

            const finalIndex = targetIndex !== -1 ? targetIndex : bestMatch;

            if (finalIndex !== -1) {
                // Use the correct window size based on expanded state for positioning
                const windowSizes = isExpanded ? CONFIG.EXPANDED_WINDOW_SIZES : CONFIG.WINDOW_SIZES;
                const windowSize = windowSizes[tempZoomLevel];
                const maxOffset = Math.max(0, aggregatedData.length - windowSize);

                let targetOffset;
                if (isButtonZoom) {
                    targetOffset = finalIndex - windowSize + 1;
                } else if (cursorXForZoom !== null) {
                    const chartSelector = isExpanded ? '[data-chart-container-modal]' : '[data-chart-container]';
                    const chartElement = document.querySelector(chartSelector);

                    if (chartElement) {
                        const rect = chartElement.getBoundingClientRect();
                        const relativeX = cursorXForZoom - rect.left;
                        const chartWidth = rect.width;
                        const leftMargin = CONFIG.CHART_MARGINS.left;
                        const rightMargin = CONFIG.CHART_MARGINS.right;
                        const plotWidth = chartWidth - leftMargin - rightMargin;

                        if (relativeX > leftMargin && relativeX < chartWidth - rightMargin) {
                            const plotRelativeX = relativeX - leftMargin;
                            const barSpacing = plotWidth / windowSize;
                            const targetBarPosition = plotRelativeX / barSpacing;
                            targetOffset = finalIndex - targetBarPosition;
                        } else {
                            targetOffset = finalIndex - Math.floor(windowSize / 2);
                        }
                    } else {
                        targetOffset = finalIndex - Math.floor(windowSize / 2);
                    }
                } else {
                    targetOffset = finalIndex - Math.floor(windowSize / 2);
                }

                const clampedOffset = Math.max(0, Math.min(targetOffset, maxOffset));
                panningState.setPanOffset(clampedOffset);
                setTargetDateForZoom(null);
                setIsButtonZoom(false);
                setCursorXForZoom(null);
            }
        }
    }, [aggregatedData, tempZoomLevel, targetDateForZoom, panningState, isExpanded]);

    // Native event listeners for wheel and touch
    useEffect(() => {
        const chartElement = document.querySelector('[data-chart-container]');
        const modalChartElement = document.querySelector('[data-chart-container-modal]');
        const activeChartElement = isExpanded && modalChartElement ? modalChartElement : chartElement;

        if (!activeChartElement) return;

        const wheelHandler = (e: Event) => {
            const wheelEvent = e as WheelEvent;

            if (wheelEvent.ctrlKey) {
                wheelEvent.preventDefault();
                const cursorX = panningState.cursorPosition.x || wheelEvent.clientX;
                if (wheelEvent.deltaY < -2) {
                    zoomControls.handleZoomIn(cursorX);
                } else if (wheelEvent.deltaY > 2) {
                    zoomControls.handleZoomOut(cursorX);
                }
                return;
            }

            const windowSizes = isExpanded ? CONFIG.EXPANDED_WINDOW_SIZES : CONFIG.WINDOW_SIZES;
            const windowSize = windowSizes[zoomControls.zoomLevel];
            if (aggregatedData.length <= windowSize) return;

            wheelEvent.preventDefault();
            const maxOffset = Math.max(0, aggregatedData.length - windowSize);
            const delta = wheelEvent.deltaX !== 0 ? wheelEvent.deltaX : wheelEvent.deltaY;
            const itemsDelta = delta * 0.01;

            panningState.setPanOffset((prev: number) => {
                const newOffset = prev + itemsDelta;
                return Math.max(0, Math.min(maxOffset, newOffset));
            });
        };

        const touchStartHandler = (e: Event) => {
            const touchEvent = e as TouchEvent;
            touchEvent.preventDefault();

            if (touchEvent.touches.length === 1) {
                const windowSize = CONFIG.WINDOW_SIZES[zoomControls.zoomLevel];
                if (aggregatedData.length <= windowSize) return;

                panningState.setIsDragging(true);
                panningState.dragStartX.current = touchEvent.touches[0].clientX;
                panningState.dragStartOffset.current = panningState.panOffset;
                panningState.setTempOffset(null);
            }
        };

        const touchMoveHandler = (e: Event) => {
            const touchEvent = e as TouchEvent;
            touchEvent.preventDefault();

            if (touchEvent.touches.length === 1 && panningState.isDragging) {
                const windowSize = CONFIG.WINDOW_SIZES[zoomControls.zoomLevel];
                if (aggregatedData.length <= windowSize) return;

                const deltaX = touchEvent.touches[0].clientX - panningState.dragStartX.current;
                const pixelsPerItem = CONFIG.WIDTH_ESTIMATE / windowSize;
                const itemsDelta = -deltaX / pixelsPerItem;
                const newOffset = panningState.dragStartOffset.current + itemsDelta;
                const maxOffset = Math.max(0, aggregatedData.length - windowSize);
                const clampedOffset = Math.max(0, Math.min(maxOffset, newOffset));

                const roundedOffset = Math.round(clampedOffset * 10) / 10;
                if (panningState.tempOffset === null || Math.abs(roundedOffset - (panningState.tempOffset || 0)) >= CONFIG.DRAG_SENSITIVITY) {
                    panningState.setTempOffset(roundedOffset);
                }
            }
        };

        const touchEndHandler = () => {
            if (panningState.tempOffset !== null) {
                panningState.setPanOffset(Math.round(panningState.tempOffset));
            }
            panningState.setIsDragging(false);
            panningState.setTempOffset(null);
        };

        const preventNavigation = (e: TouchEvent) => {
            if (panningState.isDragging || activeChartElement.contains(e.target as Node)) {
                e.preventDefault();
            }
        };

        // Add event listeners
        activeChartElement.addEventListener('wheel', wheelHandler, { passive: false });
        activeChartElement.addEventListener('touchstart', touchStartHandler, { passive: false });
        activeChartElement.addEventListener('touchmove', touchMoveHandler, { passive: false });
        activeChartElement.addEventListener('touchend', touchEndHandler, { passive: false });
        document.addEventListener('touchstart', preventNavigation, { passive: false });
        document.addEventListener('touchmove', preventNavigation, { passive: false });

        return () => {
            activeChartElement.removeEventListener('wheel', wheelHandler);
            activeChartElement.removeEventListener('touchstart', touchStartHandler);
            activeChartElement.removeEventListener('touchmove', touchMoveHandler);
            activeChartElement.removeEventListener('touchend', touchEndHandler);
            document.removeEventListener('touchstart', preventNavigation);
            document.removeEventListener('touchmove', preventNavigation);
        };
    }, [zoomControls, aggregatedData.length, panningState, isExpanded]);

    // Common chart container props
    const chartContainerProps = {
        sx: {
            height: isExpanded ? CONFIG.EXPANDED_HEIGHT - 35 : height - 35,
            width: '100%',
            overflow: 'visible',
            cursor: panningState.isDragging ? 'grabbing' : (canPan ? 'grab' : 'default'),
            userSelect: 'none',
            touchAction: 'none',
            position: 'relative',
            zIndex: 1,
            '& *': { touchAction: 'none' },
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            overscrollBehaviorX: 'contain',
        },
        onMouseDown: canPan ? eventHandlers.handleMouseDown : undefined,
        onMouseMove: panningState.isDragging ? eventHandlers.handleMouseMove : eventHandlers.handleMouseMoveTracking,
        onMouseUp: eventHandlers.handleMouseUp,
        onMouseLeave: eventHandlers.handleMouseUp,
    };

    return (
        <Box sx={{ width: '100%', height, position: 'relative', overflow: 'visible' }}>
            <ChartControls
                zoomControls={zoomControls}
                onExpandToggle={handleExpandToggle}
            />

            <ChartHeader
                metric={metric}
                onMetricChange={handleMetricChange}
            />

            {/* Main Chart */}
            {!isExpanded && (
                <Box data-chart-container {...chartContainerProps}>
                    <ChartRenderer
                        chartData={chartData}
                        tickValues={tickValues}
                        metric={metric}
                        theme={theme}
                    />
                </Box>
            )}

            {/* Expanded Modal */}
            {isExpanded && (
                <Modal
                    open={isExpanded}
                    onClose={() => setIsExpanded(false)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2,
                    }}
                >
                    <Box
                        sx={{
                            width: '80vw',
                            maxWidth: 1200,
                            height: CONFIG.EXPANDED_HEIGHT + 100,
                            maxHeight: '90vh',
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: 2,
                            boxShadow: theme.shadows[24],
                            p: 3,
                            outline: 'none',
                            overflow: 'auto',
                            position: 'relative',
                        }}
                    >
                        <Tooltip title="Collapse" placement="top">
                            <IconButton
                                onClick={() => setIsExpanded(false)}
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    zIndex: 1,
                                }}
                            >
                                <CloseFullscreen sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>

                        <Typography variant="caption" sx={{
                            display: 'block',
                            fontWeight: 600,
                            fontSize: '1.2rem',
                            lineHeight: 1.5,
                            color: 'text.primary'
                        }}>
                            Funding Activity
                        </Typography>
                        <Typography variant="caption" sx={{
                            display: 'block',
                            fontSize: '0.9rem',
                            lineHeight: 1,
                            color: 'text.secondary',
                            opacity: 0.8,
                            mb: 2
                        }}>
                            Explore funded deals
                        </Typography>

                        <Box sx={{ height: CONFIG.EXPANDED_HEIGHT }}>
                            <ChartHeader
                                metric={metric}
                                onMetricChange={handleMetricChange}
                                isModal={true}
                                zoomControls={zoomControls}
                            />

                            <Box data-chart-container-modal {...chartContainerProps}>
                                <ChartRenderer
                                    chartData={chartData}
                                    tickValues={tickValues}
                                    metric={metric}
                                    theme={theme}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Modal>
            )}
        </Box>
    );
};
